import {
  FileText, ListOrdered, Image as ImageIcon, Sparkles, Video, Film,
  Captions, Check, type LucideIcon,
} from "lucide-react";
import type { VideoExpressCampaign, VideoExpressScript, VideoExpressRender } from "@shared/schema";

/**
 * Single source of truth for deriving a VideoExpress campaign's pipeline stage
 * from its raw script/render rows. Mirrors the per-step "done" predicates the
 * server autopilot uses (server/routes.ts runVideoExpressAutopilot). Both the
 * detail-page run panel and the batch-launch progress view import these so the
 * stage labels can never drift between the two surfaces.
 */

export type AutopilotStatus =
  | "running" | "completed" | "partial" | "failed" | "cancelled" | "interrupted" | null | undefined;

const TERMINAL = new Set(["completed", "partial", "failed", "cancelled", "interrupted"]);
export const isActive = (s: AutopilotStatus) => !TERMINAL.has(String(s ?? "running"));

export type Phase =
  | "script" | "beatmap" | "storyboard" | "prompts" | "videos" | "render" | "captions" | "done";

export const PHASE_ORDER: Phase[] = [
  "script", "beatmap", "storyboard", "prompts", "videos", "render", "captions", "done",
];

export const PHASE_META: Record<Phase, { label: string; icon: LucideIcon; description: string }> = {
  script: {
    label: "Writing script",
    icon: FileText,
    description: "Writing the narration from your persona, product, and chosen angle.",
  },
  beatmap: {
    label: "Beat map",
    icon: ListOrdered,
    description: "Splitting the script into timed beats — a timestamped map of which line happens in each scene.",
  },
  storyboard: {
    label: "Storyboards",
    icon: ImageIcon,
    description: "Designing the key visual frames (storyboards) for each beat.",
  },
  prompts: {
    label: "Scene prompts",
    icon: Sparkles,
    description: "Turning each storyboard frame into a detailed prompt for the AI video model.",
  },
  videos: {
    label: "Generating video",
    icon: Video,
    description: "Generating each scene as its own short video clip.",
  },
  render: {
    label: "Final render",
    icon: Film,
    description: "Stitching the clips together with the voiceover into one video.",
  },
  captions: {
    label: "Adding captions",
    icon: Captions,
    description: "Burning word-by-word captions onto the finished video.",
  },
  done: {
    label: "Ready",
    icon: Check,
    description: "Your video is ready to watch, download, or save.",
  },
};

/**
 * Human explanations for the two campaign-level prep stages that run before any
 * per-video script exists (Creative Strategy → Concepts → first script). Kept
 * here alongside PHASE_META so every surface that narrates a VE Max run pulls
 * its copy from one place.
 */
export const PREP_STAGE_DESCRIPTION: Record<"strategy" | "concept", string> = {
  strategy: "Analyzing your brand, persona, and product to set the creative direction.",
  concept: "Drafting distinct video concepts to build the videos from.",
};

// ── Per-script predicates (client mirror of the server orchestrator) ─────────
function parseScript(s: any): any {
  try {
    return typeof s?.fullScript === "string" ? JSON.parse(s.fullScript) : (s?.fullScript || {});
  } catch {
    return {};
  }
}
const voiceReady = (s: any) => s?.voiceOverStatus === "ready" && !!s?.voiceOverUrl;
const voiceFailed = (s: any) => s?.voiceOverStatus === "failed";
const hasBeats = (s: any) => { const f = parseScript(s); return Array.isArray(f?.beats) && f.beats.length > 0; };
const beatsFailed = (s: any) => parseScript(s)?.beatsStatus === "failed";
function storyboardReady(s: any, slots: number): boolean {
  if (s?.storyboardStatus !== "ready") return false;
  if (slots >= 2 && s?.storyboardStatus2 !== "ready") return false;
  if (slots >= 3 && s?.storyboardStatus3 !== "ready") return false;
  return true;
}
function storyboardFailed(s: any, slots: number): boolean {
  return s?.storyboardStatus === "failed"
    || (slots >= 2 && s?.storyboardStatus2 === "failed")
    || (slots >= 3 && s?.storyboardStatus3 === "failed");
}

// Storyboard slot count is duration-driven (36s→1, 72s→2, 108s→3), matching the
// server's buildStoryboardCampaignContext.
export function storyboardSlots(campaign?: VideoExpressCampaign): number {
  const secs = parseInt(String((campaign as any)?.duration ?? "36"), 10) || 36;
  // Mirror the server (resolveClaymationVideoPlan): round(seconds / 36) clamped 1–3.
  return Math.min(3, Math.max(1, Math.round(secs / 36)));
}

export type ScriptState = {
  phase: Phase;
  failed: boolean;
  note?: string;
  clips?: { ready: number; total: number };
};

export function computeScriptState(
  s: VideoExpressScript,
  render: VideoExpressRender | undefined,
  slots: number,
): ScriptState {
  // Notes surface the step's PERSISTED error text (not just a generic
  // "Failed") so every failure reads as an actionable message in the UI.
  if (!voiceReady(s)) {
    const failed = voiceFailed(s);
    return { phase: "script", failed, note: failed ? ((s as any).voiceOverError || "Script failed") : undefined };
  }
  if (!hasBeats(s)) {
    const failed = beatsFailed(s);
    return { phase: "beatmap", failed, note: failed ? (parseScript(s)?.beatsError || "Beat map failed") : undefined };
  }
  if (!storyboardReady(s, slots)) {
    const failed = storyboardFailed(s, slots);
    const sbErr = (s as any).storyboardError || (s as any).storyboardError2 || (s as any).storyboardError3;
    return { phase: "storyboard", failed, note: failed ? (sbErr || "Storyboard failed") : undefined };
  }
  const sps = (s as any).scenePromptStatus;
  if (sps !== "ready") {
    const failed = sps === "error";
    return { phase: "prompts", failed, note: failed ? ((s as any).scenePromptError || "Scene prompts failed") : undefined };
  }
  // A render row only exists once every scene clip is ready, so its presence is
  // the clean boundary between the "videos" and "render" phases.
  if (!render) {
    const scenes: any[] = Array.isArray((s as any).scenePrompts) ? (s as any).scenePrompts : [];
    const vids: any[] = Array.isArray((s as any).sceneVideos) ? (s as any).sceneVideos : [];
    const ready = vids.filter((v) => v?.videoUrl || v?.clipUrl || v?.url).length;
    const total = scenes.length || vids.length || 0;
    // Clip failures were previously invisible (the phase just kept "running").
    // Once no clip is still pending, surface errored clips as a failed state.
    const errored = vids.filter((v) => v?.error).length;
    const pending = vids.some((v) => v && !v.videoUrl && !v.clipUrl && !v.url && !v.error && v.status !== "paused");
    if (errored > 0 && !pending) {
      return {
        phase: "videos",
        failed: true,
        note: `${errored} clip${errored === 1 ? "" : "s"} failed — ${vids.find((v) => v?.error)?.error || "open the campaign to re-render"}`,
        clips: total ? { ready, total } : undefined,
      };
    }
    return { phase: "videos", failed: false, clips: total ? { ready, total } : undefined };
  }
  if (render.status === "failed") return { phase: "render", failed: true, note: (render as any).error || "Render failed" };
  if (render.status !== "complete") return { phase: "render", failed: false };
  const cap = (render as any).captionedVideoStatus;
  if (cap === "failed") return { phase: "done", failed: false, note: "Captions failed — using uncaptioned video" };
  if (cap !== "ready") return { phase: "captions", failed: false };
  return { phase: "done", failed: false };
}

// ---------------------------------------------------------------------------
// Per-stage failure derivations for the campaign edit page's pipeline
// walkthrough (Scene Clips / Final Render / Captions). Kept here as pure
// helpers so the persisted-error-plus-retry wiring is unit-testable and can't
// drift from the run panel's computeScriptState.
// ---------------------------------------------------------------------------

export type ClipStepFailure = { sceneNumber: number; error: string; retryPath: string };

export function deriveClipStepState(script: any): {
  total: number;
  readyCount: number;
  pendingCount: number;
  status: "ready" | "generating" | "failed" | undefined;
  failures: ClipStepFailure[];
} {
  const vids: any[] = Array.isArray(script?.sceneVideos) ? script.sceneVideos : [];
  const failures: ClipStepFailure[] = vids
    .filter((v) => v?.error && !v?.videoUrl)
    .map((v) => ({
      sceneNumber: Number(v.scene_number),
      error: String(v.error),
      // Scoped per-scene regenerate — never the full-run route (that resets and
      // re-bills EVERY scene).
      retryPath: `/api/video-express-scripts/${script?.id}/scene-videos/${Number(v.scene_number)}/regenerate`,
    }))
    .sort((a, b) => a.sceneNumber - b.sceneNumber);
  const pendingCount = vids.filter((v) => v && !v.videoUrl && !v.error && v.status !== "paused").length;
  const readyCount = vids.filter((v) => v?.videoUrl).length;
  const status =
    vids.length === 0 ? undefined
    : pendingCount > 0 ? "generating" as const
    : failures.length > 0 ? "failed" as const
    : readyCount === vids.length ? "ready" as const
    : undefined;
  return { total: vids.length, readyCount, pendingCount, status, failures };
}

export function deriveRenderStepState(script: any, render: any, clipsReady: boolean): {
  visible: boolean;
  status: string | undefined;
  error: string | undefined;
  retryPath: string;
  canRetry: boolean;
} {
  const raw = render?.status as string | undefined;
  return {
    // Show the stage once a render exists OR the clips are ready to stitch.
    visible: !!render || clipsReady,
    status: !render ? undefined
      : raw === "complete" ? "ready"
      : raw === "queued" || raw === "rendering" ? "generating"
      : raw,
    error: raw === "failed" ? (render?.error || "Final render failed.") : undefined,
    retryPath: `/api/video-express-scripts/${script?.id}/render`,
    // The render route requires every clip to be ready.
    canRetry: clipsReady,
  };
}

export function deriveCaptionStepState(render: any): {
  visible: boolean;
  status: string | undefined;
  error: string | undefined;
  retryPath: string;
} {
  const raw = render?.captionedVideoStatus as string | undefined;
  const visible = !!render && !!raw && raw !== "idle";
  return {
    visible,
    status: !visible ? undefined : raw === "ready" ? "ready" : raw === "failed" ? "failed" : "generating",
    error: raw === "failed" ? (render?.captionedVideoError || "Caption generation failed.") : undefined,
    retryPath: render ? `/api/video-express-renders/${render.id}/generate-captions` : "",
  };
}

export function phasePercent(phase: Phase): number {
  if (phase === "done") return 100;
  const idx = PHASE_ORDER.indexOf(phase);
  return Math.round((idx / (PHASE_ORDER.length - 1)) * 100);
}

export function statusPill(status: AutopilotStatus): { label: string; className: string } {
  switch (status) {
    case "completed": return { label: "Completed", className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" };
    case "partial": return { label: "Finished with issues", className: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30" };
    case "failed": return { label: "Failed", className: "bg-destructive/15 text-destructive border-destructive/30" };
    case "cancelled": return { label: "Cancelled", className: "bg-muted text-muted-foreground border-border" };
    case "interrupted": return { label: "Interrupted", className: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30" };
    default: return { label: "Running", className: "bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30" };
  }
}
